import React, { useState, useEffect } from 'react';
import { Router, Route, Redirect, Switch } from 'react-router-dom';
import { Provider } from 'react-redux';
import { Chart } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { MatrixController, MatrixElement } from 'chartjs-chart-matrix';
import configureStore from './store/configureStore';
import { history } from './_helpers';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { PrivateRoute } from './_components';
import { HealthPage } from './views/HealthPage';
import { AdminPage, AdminNotification } from './views/AdminPage';
import { Activate } from './views/LoginPage/Activate';
import { TwoFactorAuth } from './views/LoginPage/TwoFactorAuth';
import { ResetPassword } from './views/LoginPage/ResetPassword';
import { PageNotFound } from './views/PageNotFound/';
import { Account } from './views/Account';
import { CustomerPreferences } from './views/CustomerPage/CustomerPreferences';
import { CustomerStatement } from './views/CustomerPage/CustomerStatement';
import { CustomerSupport } from './views/CustomerPage/CustomerSupport';
import { FindFreeCharger } from './views/CustomerPage/FindFreeCharger';
import { ActivateLance } from './views/CustomerPage/ActivateLance';
import { ChargingSession } from './views/CustomerPage/ChargingSession';
import { ChargingRates } from './views/CustomerPage/ChargingRates';
import { ConvertHUBToAON } from './views/CustomerPage/AON/ConvertHUBToAON';
import { Onboard } from './views/CustomerPage/Onboard';
import { HUBToAONSelect } from './views/CustomerPage/HUBToAON/HUBToAONSelect';
import { HUBToAONEOISubmitted } from './views/CustomerPage/HUBToAON/HUBToAONEOISubmitted';
import { HUBToAONEOIApproved } from './views/CustomerPage/HUBToAON/HUBToAONEOIApproved';
import { HUBToAONEOIRejected } from './views/CustomerPage/HUBToAON/HUBToAONEOIRejected';
import { AONSurvey } from './views/CustomerPage/AON/AONSurvey';
import { Layout } from './_components/layout/Layout';
import { ToastProvider } from './_components/toast/ToastProvider';
import { CustomerLoginPage, CustomerForgotPassword } from './views/LoginPage';
import {
  DocController,
  DocGuidelines,
  DocPolicies,
  DocStandards,
  DocTechnical,
  DocSupportHUB,
  DocSupportAON
} from './doc';
import { Cascade } from './doc/teams/Cascade';
import { authAPI } from './_services';
import { useCurrentUser } from './_services/hooks/user';
import Moment from 'moment-timezone';
import { extendMoment } from 'moment-range';
import { dbUTCDatetimeToLocal, elucidate } from './utils';
import { speechHandler } from './speech';
import { AonStatement } from './views/CustomerPage/AON/AonStatement';

// App cache: get version from browser cache
let cachedVersion = { version: 'unknown' };
try {
  cachedVersion = require('./version');
} catch (error) {}

global.speech = speechHandler;
global.geoWatch = null;

Chart.register(ChartDataLabels, MatrixController, MatrixElement);
Chart.defaults.set('plugins.datalabels', { display: false });

const initialState = {};
const store = configureStore(initialState);

const Config = require('./conf');

const moment = extendMoment(Moment);

// 2021.10.22 - Safari does not currently support BroadcastChannel
let trojanBroadcast = null;

// A2HS (App to home screen) installer
let deferredPrompt;
window.addEventListener('beforeinstallprompt', e => {
  // Prevent Chrome 67 and earlier from automatically showing the prompt
  e.preventDefault();
  // Stash the event so it can be triggered later.
  deferredPrompt = e;
  // Update UI to notify the user they can add to home screen
  const addBtn = document.querySelector('.home-screen');
  if (!addBtn) return;

  addBtn.style.display = 'block';

  addBtn.addEventListener('click', e => {
    // hide our user interface that shows our A2HS button
    addBtn.style.display = 'none';
    // Show the prompt
    deferredPrompt.prompt();
    // Wait for the user to respond to the prompt
    deferredPrompt.userChoice.then(choiceResult => {
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the A2HS prompt');
      } else {
        console.log('User dismissed the A2HS prompt');
      }
      deferredPrompt = null;
    });
  });
});

const App = () => {
  const [appUpdated, setAppUpdated] = useState(false);
  const [stripePromise, setStripePromise] = useState(() => null);
  const {
    currentUser,
    currentUserIsAdmin,
    currentUserIsCustomer
  } = useCurrentUser();

  useEffect(() => {
    setStripePromise(loadStripe(Config.Local().stripePublicKey));
  }, []);

  const notifications = localStorage.getItem('notifications');
  if (!notifications) {
    localStorage.setItem('notifications', JSON.stringify({ data: [] }));
  }

  if (currentUser) {
    if (!trojanBroadcast) {
      try {
        trojanBroadcast = new BroadcastChannel('trojan-radio');

        // Enable feature testing only for specific users
        // if (
        //   currentUser.user_name === 'tom' ||
        //   currentUser.user_name === 'test_customer@trojanenergyltd.com'
        // ) {
        //   trojanBroadcast.postMessage({
        //     type: 'admin',
        //     message: 'RESTRICTED_FEATURE',
        //     link: ''
        //   });
        // }

        trojanBroadcast.onmessage = event => {
          console.log(`App: Broadcast message received: ${event.data.message}`);

          const notifications = JSON.parse(
            localStorage.getItem('notifications')
          );
          event.data.time = dbUTCDatetimeToLocal(
            moment.utc().format('YYYY.MM.DD @ HH:mm')
          );

          // Remember the last 50 notifications
          if (notifications.data.length > 50) {
            notifications.data = notifications.data.slice(0, -1);
          }

          // Prepend to show most recent notification first
          notifications.data = [event.data, ...notifications.data];
          localStorage.setItem('notifications', JSON.stringify(notifications));

          localStorage.setItem('newNotification', 'true');
        };
        console.log('App: Notifications BroadcastChannel running');
      } catch (e) {
        console.log('App: Notifications BroadcastChannel not running');
      }
    }
  }

  // App cache: check for new version and flag in local storage
  fetch(`/version.json?${new Date().getTime()}`, { cache: 'no-cache' })
    .then(response => response.json())
    .then(latestVersion => {
      const latest = latestVersion ? latestVersion.version : 'unknown';
      const current = cachedVersion.version;

      if (latest !== current) {
        console.log(
          `New version: ${latest}, cached version: ${current}. Clearing cache and will reload`
        );

        const notifications = localStorage.getItem('notifications');
        if (!notifications) {
          localStorage.setItem('notifications', JSON.stringify({ data: [] }));
        }

        localStorage.setItem('appInfo', JSON.stringify({ version: latest }));

        if (caches) {
          caches.keys().then(names => {
            names.forEach(name => {
              caches.delete(name);
            });
          });
        }

        localStorage.setItem('appInfo', JSON.stringify({ version: latest }));
        localStorage.setItem('appUpdated', 'true');
      }
    })
    .catch(error => {});

  if (appUpdated) {
    return (
      <>
        <br />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <span style={{ color: 'white' }}>
            A new update of the portal is available. Refresh your browser, or
            drag page down on mobile.
          </span>
        </div>
      </>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <Provider store={store}>
        <Router history={history}>
          <Layout>
            <Switch>
              <Route
                exact
                path="/"
                render={() => {
                  const currentUser = JSON.parse(elucidate('curruser'));
                  if (!currentUser)
                    return <Redirect to={{ pathname: '/login' }} />;

                  if (currentUserIsCustomer) authAPI.loginCustomer(currentUser);

                  return <Redirect to={{ pathname: '/admin' }} />;
                }}
              />
              <Route
                path="/health"
                title="Status - Trojan Energy"
                component={HealthPage}
              />
              <Route
                path="/admin/notif"
                title="Notification - Trojan Energy"
                component={AdminNotification}
              />
              <PrivateRoute
                path="/admin"
                roles={[
                  'Admin',
                  'CustomerSupport',
                  'Support',
                  'Executive',
                  'Monitor'
                ]}
                component={AdminPage}
              />
              <Route
                path="/activate"
                title="Activate Account - Trojan Energy"
                component={Activate}
              />
              <Route
                path="/login/activate"
                title="Login - Trojan Energy"
                component={CustomerLoginPage}
              />
              <Route
                path="/login"
                title="Login - Trojan Energy"
                component={CustomerLoginPage}
              />
              <Route
                path="/logout"
                ctitle="Logout - Trojan Energy"
                render={() => {
                  authAPI.logout();
                  history.push('/login');
                }}
              />
              <Route
                path="/login_tfa"
                title="Login 2FA - Trojan Energy"
                component={TwoFactorAuth}
              />
              <Route
                path="/resetPassword"
                ctitle="Change Password - Trojan Energy"
                component={ResetPassword}
              />
              <Route
                path="/forgotPassword"
                ctitle="Forgot Password - Trojan Energy"
                component={CustomerForgotPassword}
              />
              <PrivateRoute
                path="/customer/preferences"
                title="Customer Preferences - Trojan Energy"
                component={CustomerPreferences}
              />
              <PrivateRoute
                path="/customer/statement"
                title="Customer Statement - Trojan Energy"
                component={CustomerStatement}
              />
              <PrivateRoute
                path="/customer/aon/statement"
                title="HOME Statement - Trojan Energy"
                component={AonStatement}
              />
              <PrivateRoute
                path="/customer/deicer"
                title="Find Free Charge Point - Trojan Energy"
                component={FindFreeCharger}
              />
              <PrivateRoute
                path="/customer/activate_lance"
                title="Register Lance - Trojan Energy"
                component={ActivateLance}
              />
              <PrivateRoute
                path="/customer/charging"
                title="Charging Session - Trojan Energy"
                component={ChargingSession}
              />
              <PrivateRoute
                path="/customer/support"
                title="Customer Support - Trojan Energy"
                component={CustomerSupport}
              />
              <PrivateRoute
                path="/customer/charging_rates"
                title="Charging Rates - Trojan Energy"
                component={ChargingRates}
              />
              <PrivateRoute
                path="/customer/dashboard"
                title="Dashboard - Trojan Energy"
                component={Onboard}
              />
              <PrivateRoute
                path="/customer/terms"
                title="Terms And Conditions - Trojan Energy"
                component={Onboard}
              />
              <Route
                path="/customer/conversion"
                title="Trojan Home Transition - Trojan Energy"
                component={ConvertHUBToAON}
              />
              <Route
                path="/customer/aon_survey"
                title="Trojan Home Survey - Trojan Energy"
                component={AONSurvey}
              />
              <PrivateRoute
                path="/customer/conversion_select"
                title="HUB/Home Conversion"
                component={HUBToAONSelect}
              />
              <PrivateRoute
                path="/customer/conversion_eoi_submitted"
                title="HUB/Home Conversion - EOI Submitted"
                component={HUBToAONEOISubmitted}
              />
              <PrivateRoute
                path="/customer/conversion_eoi_approved"
                title="HUB/Home Conversion - EOI Approved"
                component={HUBToAONEOIApproved}
              />
              <PrivateRoute
                path="/customer/conversion_eoi_rejected"
                title="HUB/Home Conversion - EOI Rejected"
                component={HUBToAONEOIRejected}
              />
              <PrivateRoute
                exact
                path="/account"
                title="Customer Account - Trojan Energy"
                component={Account}
              />
              <PrivateRoute
                path="/account/:id"
                title="Customer Account - Trojan Energy"
                component={Account}
              />
              <PrivateRoute
                exact
                path="/doc_controller"
                roles={['Admin', 'CustomerSupport', 'Executive', 'Support']}
                component={DocController}
              />
              <PrivateRoute
                exact
                path="/doc_guidelines"
                roles={['Admin', 'CustomerSupport', 'Support', 'Executive']}
                component={DocGuidelines}
              />
              <PrivateRoute
                exact
                path="/doc_policies"
                roles={['Admin', 'CustomerSupport', 'Support', 'Executive']}
                component={DocPolicies}
              />
              <PrivateRoute
                exact
                path="/doc_standards"
                roles={['Admin', 'CustomerSupport', 'Support', 'Executive']}
                component={DocStandards}
              />
              <PrivateRoute
                exact
                path="/doc_technical"
                roles={['Admin', 'CustomerSupport', 'Support', 'Executive']}
                component={DocTechnical}
              />
              <PrivateRoute
                exact
                path="/doc_support_hub"
                roles={['Admin', 'CustomerSupport', 'Support', 'Executive']}
                component={DocSupportHUB}
              />
              <PrivateRoute
                exact
                path="/doc_support_aon"
                roles={['Admin', 'CustomerSupport', 'Support', 'Executive']}
                component={DocSupportAON}
              />
              <PrivateRoute
                exact
                path="/team_cascade"
                roles={['Admin', 'Support']}
                authorised={currentUserIsAdmin}
                component={Cascade}
              />
              <Route component={PageNotFound} />
            </Switch>
            {/* </Container> */}
          </Layout>
          {/* </div> */}
          <ToastProvider />
        </Router>
      </Provider>
    </Elements>
  );
};

export default App;
